/**
 * @license
 * @copyright Copyright Motili Inc., 2017 All Rights Reserved
 */

import { useEffect } from 'react';
import PropTypes from 'prop-types';

Modal.propTypes = {
    controlId: PropTypes.string,
    style: PropTypes.shape({
        modal: PropTypes.shape({
            display: PropTypes.string,
            position: PropTypes.string,
            zIndex: PropTypes.number,
            paddingTop: PropTypes.string,
            left: PropTypes.number,
            top: PropTypes.number,
            width: PropTypes.string,
            height: PropTypes.string,
            overflow: PropTypes.string,
            backgroundColor: PropTypes.string,
        }),
    }),
    className: PropTypes.string,
    header: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
    subHeader: PropTypes.string,
    showClose: PropTypes.bool,
    onCloseClick: PropTypes.func.isRequired,
    subCloseIconMessage: PropTypes.string,
    children: PropTypes.node,
    footer: PropTypes.element,
    inline: PropTypes.bool,
    requiredFields: PropTypes.bool,
    headerStyles: PropTypes.shape({
        fontSize: PropTypes.number,
        fontWeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
    subHeaderStyles: PropTypes.shape({
        fontSize: PropTypes.number,
        fontWeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
    headerDivider: PropTypes.bool,
};

Modal.defaultProps = {
    className: '',
    controlId: 'MotiliModal',
    style: {},
    showClose: false,
    inline: false,
    headerStyles: { fontSize: 21, fontWeight: 600 },
    subHeaderStyles: { fontSize: 16, fontWeight: 'normal' },
};

/**
 * Modal
 * @param {Object} props
 * @param {String} props.className - className applied to modal
 * @param {Object} props.style - style object applied to modal
 * @param {String} props.header - Header text
 * @param {String} props.subHeader - Subheader text
 * @param {Component<Object>} props.footer - footer component
 */
export default function Modal(props) {
    const onClose = e => {
        if (
            e.target.closest(`#${props.controlId}`) !==
            document.getElementById(props.controlId)
        ) {
            // eslint-disable-line no-undef
            return props.onCloseClick(e);
        }
    };

    return (
        <div id={props.controlId} style={styles.modal}>
            <div
                style={{ ...styles.modalContent, ...props.style }}
                className={props.className}
            >
                {!props.inline && (
                    <>
                        <Header {...props} />
                        {props.headerDivider && <DividerLine />}
                        {props.children}
                        {props.footer && <Footer {...props} />}
                    </>
                )}
                {props.inline && (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {props.showClose && (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'flex-end',
                                }}
                            >
                                <i
                                    onClick={e => _onCloseClick(e, props)}
                                    className='fa fa-angle-times'
                                    style={{
                                        color: '#3b3b3b',
                                        cursor: 'pointer',
                                        marginRight: 10,
                                    }}
                                />
                            </div>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'row' }}>
                            {props.children}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function Header(props) {
    return (
        <div>
            <div
                style={
                    props.headerDivider
                        ? { ...styles.headerContent, marginBottom: 0 }
                        : styles.headerContent
                }
            >
                {props.header && (
                    <div style={{ flex: 1, ...props.headerStyles }}>
                        {props.header}
                        <div style={{ ...props.subHeaderStyles }}>
                            {props.subHeader}
                        </div>
                    </div>
                )}
                {props.showClose && (
                    <i
                        onClick={e => _onCloseClick(e, props)}
                        className='fa fa-angle-times'
                        style={styles.closeButton}
                    />
                )}
                {props.subCloseIconMessage && (
                    <div
                        className='subCloseIconMessage'
                        style={{
                            ...styles.subCloseIconMessage,
                            ...props.subCloseIconMessageStyles,
                        }}
                    >
                        {props.subCloseIconMessage}
                    </div>
                )}
            </div>
        </div>
    );
}
Header.propTypes = Modal.propTypes;

function Footer(props) {
    return <div style={{ margin: '30px 0' }}>{props.footer}</div>;
}
Footer.propTypes = Modal.propTypes;

const DividerLine = () => {
    return (
        <div
            style={{
                borderBottom: '0.5px solid #818181',
                width: '100%',
            }}
        />
    );
};

function _onCloseClick(e, props) {
    return props.onCloseClick(e);
}

const styles = {
    modal: {
        display: 'block',
        width: '100%' /* Full width */,
        height: '100%' /* Full height */,
        overflow: 'auto' /* Enable scroll if needed */,
    },
    modalContent: {
        position: 'relative',
        zIndex: 3 /* Sit on top */,
        backgroundColor: 'white',
        margin: 'auto',
        width: '80%',
        fontSize: 14,
        maxWidth: 850,
        padding: '20px 20px 20px 15px',
        borderRadius: '4px',
        boxShadow: '0 2px 12px 0 rgba(0, 0, 0, 0.25)',
    },
    headerContent: {
        display: 'flex',
        alignItems: 'center',
        fontSize: 21,
        color: '#3b3b3b',
        paddingBottom: 10,
        marginBottom: 10,
    },
    closeButton: {
        position: 'absolute',
        right: 20,
        top: 15,
        fontSize: 25,
        cursor: 'pointer',
        color: 'gray',
    },
    subCloseIconMessage: {
        right: '17px',
        top: '60px',
        position: 'absolute',
        color: 'gray',
    },
    requiredFieldsText: {
        color: '#3f82a5',
        fontSize: 14,
        fontWeight: 'bold',
        fontStyle: 'italic',
    },
};
